import React, { useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import find from 'lodash/find';
import ProfileSettings from '@Services/ProfileSettings';
import classnames from 'classnames';
import { ProfileSettingsConfig } from '@Constants/rx-user/constants';

import { setImmutable } from '@Pages/ProfileSettings/Preferences/utils/setImmutable';
import { TextInput } from '@els/els-react--text-input';
import { RadioButtonGroup } from '@els/els-react--radio-button';
import { Checkbox } from '@els/els-react--checkbox';
import { SlideSwitch } from '@els/els-react--slide-switch';
import RPSSwitchIcon from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/RPSSwitchIcon';
import {
  isRCSAvailable as isRCSAvailableFn,
  isRPSAvailable as isRPSAvailableFn,
  isRPSChecked as isRPSCheckedFn,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/RPS/mapRCSToRPS';
import PreferencesTooltip from '@Pages/ProfileSettings/Preferences/components/PreferencesTooltip';
import ProfileSettingsAccordionItem from '@Pages/ProfileSettings/Components/ProfileSettingsAccordionItem';
import {
  IK_PARAMS_MAX_NUMBER_OF_ROUTES_INFO_LABEL,
  IK_PARAMS_SELECTIVITY_INFO_LABEL,
  IK_PARAMS_MAX_SHIPPING_TIME,
  IK_PARAMS_SMS_INFO_LABEL,
  RCSANY,
  RCS10D,
  maxShippingTimeDisplayHelperObj,
  maxShippingTimeToggleHelperObj,
  PREDICTED_PARAMS_MAX_PRICE_LABEL,
  RPS_LIBRARIES,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/constants';
import { getPredictedProcessingTimeLabelByValue, getPredictedProcessingTimeIntervalUpdatedValue } from '@Pages/RetroSynthesis/utils';
import { PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS } from '@Pages/RetroSynthesis/constants';

import './SynthesisPlannerPredicted.scss';

const buildPredictionPlanOptions = (editUser) => {
  const PREDICTION_PLAN_LIBRARIES_KEY = 'PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES';
  const combinedInfo = ProfileSettings.getCombinedInfo();
  const optionsObject = ProfileSettings?.getModelValue(combinedInfo, PREDICTION_PLAN_LIBRARIES_KEY).preferences;
  const keys = optionsObject ? Object.keys(optionsObject) : [];
  let predictionPlanOptions = keys.map((option) => {
    const fullKey = `${PREDICTION_PLAN_LIBRARIES_KEY}.${option}`;
    const values = get(optionsObject, [option, 'values']);
    const onlyOneValue = values && values.length === 1 ? get(values, [0, 'value']) : null;
    const explicitlyDefinedValue = ProfileSettings.getModelValue(editUser, fullKey);
    const defaultValue = values
      ? get(
          values.find((value) => value && value.defaultValue),
          ['value']
        )
      : null;

    let value;
    if (!isNil(onlyOneValue)) {
      value = onlyOneValue;
    } else if (!isNil(explicitlyDefinedValue)) {
      value = explicitlyDefinedValue;
    } else {
      value = defaultValue;
    }

    return {
      label: optionsObject[option]?.label,
      name: optionsObject[option]?.name,
      property: `RX2_PREDICTION_PLAN_LIBRARIES.${option}`,
      id: `prediction_plan_option_${option}`,
      disabled: !isNil(onlyOneValue),
      /* If option has only one value - use this value,
       * if more than one - check if it explicitly defined in /extended response::
       *   if yes - use value from /extended response
       *   if no - use defaultValue from /cobinedinfo
       */
      value,
    };
  });

  // Exclude RPS libraries
  predictionPlanOptions = predictionPlanOptions.filter((option) => !RPS_LIBRARIES.includes(option.name));

  return predictionPlanOptions;
};

const getDefaultNumOfSteps = (options) => ProfileSettings.parseDefaultValue(options?.RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS.values);

const getInitialNumOfStepsInputValue = (preferences, options) =>
  preferences.RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS === '1' ? getDefaultNumOfSteps(options) : preferences.RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS;

function SynthesisPlannerPredictedIktos({
  preferences,
  options,
  editUser,
  onChange = () => {},
  onSave = () => {},
  onReset = () => {},
  onClose = () => {},
}) {
  const [predictionPlanOptions, setPredictionPlanOptions] = useState([]);
  const [toggleReset, setToggleRest] = useState(false);
  const [firstDisconnect, setFirstDisconnect] = useState(preferences.RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS === '1');
  const [maxNumOfSteps, setMaxNumOfSteps] = useState(getInitialNumOfStepsInputValue(preferences, options));
  const defaultMaxNumOfSteps = useRef(getDefaultNumOfSteps(options)); // default max steps to reset for invalid value

  const isRCSAvailable = useMemo(() => isRCSAvailableFn(options), [options]);
  const isRPSAvailable = useMemo(() => isRPSAvailableFn(options), [options]);

  useMemo(() => {
    const builtPredictionPlanOptions = buildPredictionPlanOptions(editUser);
    setPredictionPlanOptions([...builtPredictionPlanOptions]);
  }, [editUser]);
  const postReset = () => {
    // To handle validation when BE sends "0" instead of "" for default value of X2_PREDICTION_PLAN_IKTOS_PRICE
    setToggleRest(!toggleReset);
  };
  useEffect(() => {
    if (`${preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE}` === '0') onChange('RX2_PREDICTION_PLAN_IKTOS_PRICE', '');
    return () => {
      onChange('isRCSUnchecked', undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (`${preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE}` === '0') onChange('RX2_PREDICTION_PLAN_IKTOS_PRICE', '');
    onChange('isRCSUnchecked', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleReset]);
  const enabledLibraries = useMemo(
    () => (predictionPlanOptions ? predictionPlanOptions.filter((lib) => !!lib.value) : []),
    [predictionPlanOptions]
  );
  const enabledLibrariesWithoutRcs = enabledLibraries.filter((lib) => [RCS10D, RCSANY].indexOf(lib.name) < 0);
  const enabledLibrariesRcs = enabledLibraries.filter((lib) => [RCS10D, RCSANY].indexOf(lib.name) >= 0);
  const hasAnyLibrariesWithoutRcs = enabledLibrariesWithoutRcs.length > 0;
  const checkValidMaxNoSteps = (value) => !Number.isNaN(value) && Number(value) % 1 === 0 && Number(value) >= 2 && Number(value) <= 15;

  const checkValidMaxPriceOfGrams = (value = '') => {
    if (!value) return true;
    if (`${value}`.replace(/\s/g, '') === '') return true;
    return !Number.isNaN(value) && Number(value) > 0 && Number(value) % 1 === 0;
  };

  const showRCSCheckbox = () => {
    if (predictionPlanOptions.length <= 1) {
      return false;
    }
    return !(
      predictionPlanOptions.length === 2 &&
      find(predictionPlanOptions, (item) => item.name === RCS10D) &&
      find(predictionPlanOptions, (item) => item.name === RCSANY)
    );
  };

  const isCustomBuildingBlocksAvailable = () =>
    predictionPlanOptions.filter((block) => [RCS10D, RCSANY].indexOf(block.name) < 0).length > 0;

  const renderValue = () => [
    {
      title: ProfileSettingsConfig.Labels.maxNumberOfSteps,
      value: firstDisconnect ? 'First disconnect only' : `${preferences.RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS} steps per route (up to)`,
    },
    {
      title: ProfileSettingsConfig.Labels.stereo,
      value: `${!preferences.RX2_PREDICTION_PLAN_IKTOS_STEREO ? 'Stereochemistry supported' : 'Stereochemistry ignored'}`,
    },
    {
      title: ProfileSettingsConfig.Labels.regioSelectivity,
      value: `${preferences.RX2_PREDICTION_PLAN_IKTOS_REGIO ? 'Regioselective reactions only' : 'Regioselectivity ignored'}`,
    },
    {
      title: ProfileSettingsConfig.Labels.maxShippingTime,
      value:
        enabledLibrariesRcs &&
        enabledLibrariesRcs.length > 0 &&
        `RCS: ${
          enabledLibrariesRcs && enabledLibrariesRcs[0] && enabledLibrariesRcs[0].name === RCS10D
            ? 'delivery time up to 10 days'
            : 'any delivery time'
        }`,
    },
    {
      title: ProfileSettingsConfig.Labels.maxPricePerGram,
      value: `RCS: ${
        preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE && `${preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE}` !== '0'
          ? `${preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE} ($/g)`
          : 'no'
      } price limit`,
    },
    {
      title: ProfileSettingsConfig.Labels.usedBuildingBlocks,
      value:
        hasAnyLibrariesWithoutRcs &&
        `${ProfileSettingsConfig.Labels.usedBuildingBlocks} = ${enabledLibrariesWithoutRcs.map(({ name }) => name)}`,
    },
    {
      title: ProfileSettingsConfig.Labels.processingTime,
      value: `${ProfileSettingsConfig.Labels.processingTime} = ${getPredictedProcessingTimeLabelByValue(
        preferences.RX2_PREDICTION_PLAN_IKTOS_PROCESSING_TIME,
        PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS
      )}`,
    },
  ];

  const handlePlanOptionChange = (optionIdx, value) => {
    const option = predictionPlanOptions?.filter((lib) => [RCS10D, RCSANY].indexOf(lib.name) < 0)[optionIdx];
    onChange(option.property, value);
    const predictionPlanOptionIndex = predictionPlanOptions.findIndex((item) => item.name === option.name);
    setPredictionPlanOptions((prev) => setImmutable(prev, `${predictionPlanOptionIndex}.value`, value));
  };
  const handleMaxShippingTimeChange = (optionIdx, value) => {
    const option = predictionPlanOptions?.filter((lib) => [RCS10D, RCSANY].indexOf(lib.name) >= 0)[optionIdx];
    const alternateOption = predictionPlanOptions.find((item) => item.name === maxShippingTimeToggleHelperObj[option.name]);
    onChange(option.property, value);
    onChange(alternateOption.property, !value);
    const predictionPlanOptionIndex = predictionPlanOptions.findIndex((item) => item.name === option.name);
    const predictionPlanAlternateOptionIndex = predictionPlanOptions.findIndex((item) => item.name === alternateOption.name);
    setPredictionPlanOptions((prev) => setImmutable(prev, `${predictionPlanOptionIndex}.value`, value));
    setPredictionPlanOptions((prev) => setImmutable(prev, `${predictionPlanAlternateOptionIndex}.value`, !value));
  };

  const handleNumOfStepsInputChange = (value) => {
    setMaxNumOfSteps(value);
    onChange('RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS', value);
  };

  const handleFirstDisconnectChange = (value) => {
    setFirstDisconnect(value);
    if (value && !checkValidMaxNoSteps(maxNumOfSteps)) {
      setMaxNumOfSteps(defaultMaxNumOfSteps.current);
    }
    onChange('RX2_PREDICTION_PLAN_IKTOS_MAXSTEPS', value ? '1' : maxNumOfSteps);
  };

  const isRCSChecked = () => enabledLibrariesRcs?.length > 0 && !preferences.isRCSUnchecked;

  const isSingleLibCheckBoxChecked = () => {
    if (!enabledLibrariesWithoutRcs) return false;
    const isRCSCheckboxChecked = isRCSChecked();
    const noOfUserBBChecked = enabledLibrariesWithoutRcs.length;
    return !!((isRCSCheckboxChecked && noOfUserBBChecked === 0) || (!isRCSCheckboxChecked && noOfUserBBChecked === 1));
  };
  const isValidMaxNumberOfSteps = checkValidMaxNoSteps(maxNumOfSteps);
  const isValidMaxPricePerGram = checkValidMaxPriceOfGrams(preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE);
  const processingTimeValue = getPredictedProcessingTimeIntervalUpdatedValue(
    Number(preferences.RX2_PREDICTION_PLAN_IKTOS_PROCESSING_TIME),
    PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS
  );

  useEffect(() => {
    // Set RPS toggle switch
    // Map object to array { RPS10D: true, RPSANY: false } to [RPS10D]
    const predictionPlanPreferences = preferences?.RX2_PREDICTION_PLAN_LIBRARIES || {};
    // eslint-disable-next-line custom/no-unsafe-deep-access
    const enabledLibrariesFromPreferences = Object.keys(predictionPlanPreferences).filter(
      (preference) => predictionPlanPreferences[preference]
    );
    const isRPSChecked = isRPSCheckedFn(enabledLibrariesFromPreferences);
    onChange('isRPSChecked', isRPSChecked);
  }, []);

  const onRPSSwitchChange = (event) => {
    const checked = event?.currentTarget?.checked;
    onChange('isRPSChecked', checked);
  };

  return (
    <ProfileSettingsAccordionItem
      onClose={() =>
        onClose(() => {
          postReset();
        })
      }
      onSave={onSave}
      label={ProfileSettingsConfig.Labels.predictionDefaultSettings}
      value={renderValue()}
      resetButton
      titled
      onReset={(subSection, closeCallback) =>
        onReset(subSection, () => {
          closeCallback();
          postReset();
        })
      }
      validateOnSave
      formValid={isValidMaxNumberOfSteps && isValidMaxPricePerGram}
      className="synthesis-planner-predicted-accordion-item preferences-accordion-item">
      <div className="preferences-accordion-item__wrapper">
        <PreferencesTooltip
          name="length-of-routes"
          label="Length of routes"
          ariaLabel={IK_PARAMS_MAX_NUMBER_OF_ROUTES_INFO_LABEL}
          content={[
            <span key="length-of-routes">
              The algorithm aims to prepare synthesis routes for a target molecule within a 10-minute time limit. Only routes beginning with
              commercially or custom available starting materials are shown. A shorter maximum route length prompts the algorithm to halt
              route candidates sooner and explore alternative options faster. Consequently, shorter routes are typically more diverse but
              not lengthy. However, if the maximum route length is too short for the algorithm to find routes back to the chosen starting
              materials, no results may be produced. Please note: it is possible to enter between 2 to 15 No of steps (the default is 15).
            </span>,
            <span key="first-disconnect">
              The option &quot;First Disconnect only&quot; will create synthesis suggestions for a given target for idea generation, but
              only show the first disconnection reaction step of a full route, without taking building block availability into account. The
              list of first disconnection step reaction can become very high (several hundred suggestions). When scanning through the
              suggestions in the routes overview or TreeView page you may select the ones you want to investigate further by clicking the
              corresponding starting material and select &quot;Create retrosynthesis plans&quot; from the 3-dot context menu of the given
              structure.
            </span>,
          ]}
        />
        <fieldset className="synthesis-planner-predicted__fieldset">
          <legend className="sr-only">Length of routes</legend>
          <div className="preferences-accordion-item__option iktos-preference-sub-row">
            <RadioButtonGroup.Option
              data-e2e="checkbox-fullSynthesisPlan-yes"
              checked={!firstDisconnect}
              label={<span>Max. Number of steps: </span>}
              onChange={(selected) => {
                if (selected) {
                  handleFirstDisconnectChange(false);
                }
              }}
            />
            <TextInput
              value={maxNumOfSteps}
              label="Provide Max number of steps ranging from 2 to 15"
              shouldHideLabel
              data-e2e="iktos-length-of-routes-input"
              isLabelInline
              min={2}
              max={15}
              disabled={firstDisconnect}
              size="small"
              className="iktos-length-of-routes-input"
              onChange={(e) => handleNumOfStepsInputChange(e.target?.value)}
            />
            (2-15)
          </div>

          <div
            aria-live="assertive"
            aria-atomic="true"
            className={classnames('length-of-routes-validation-messege', {
              'not-visible': isValidMaxNumberOfSteps,
            })}>
            {isValidMaxNumberOfSteps ? '' : 'Please enter a value between 2-15'}
          </div>

          <div className="preferences-accordion-item__option iktos-preference-sub-row">
            <RadioButtonGroup.Option
              data-e2e="checkbox-fullSynthesisPlan-yes"
              checked={!!firstDisconnect}
              label={<span>First disconnect only</span>}
              onChange={(selected) => {
                if (selected) {
                  handleFirstDisconnectChange(true);
                }
              }}
            />
          </div>
        </fieldset>

        <hr className="preferences-accordion-item__separator" />
        <PreferencesTooltip
          name="selectivity"
          label="Stereo and Regioselectivity"
          ariaLabel={IK_PARAMS_SMS_INFO_LABEL}
          content={[
            <span key="stereo">
              Stereochemistry is always considered if drawn explicitly. In case you get no results with steric substances please check
              “Ignore stereochemistry” and run the project again, to increase the probability to find routes.
            </span>,
            <span key="regio">
              If this option is selected, then the retrosynthesis algorithm will avoid routes which contain reaction steps with uncertain
              regioselectivity. Typically, this will result in a lower number of predicted routes.
            </span>,
          ]}
        />

        <div className="preferences-accordion-item__option iktos-preference-sub-row">
          <Checkbox
            data-e2e="checkbox-stereo"
            checked={!!preferences.RX2_PREDICTION_PLAN_IKTOS_STEREO || false}
            label="Ignore stereochemistry"
            onChange={() => onChange('RX2_PREDICTION_PLAN_IKTOS_STEREO', !preferences.RX2_PREDICTION_PLAN_IKTOS_STEREO)}
          />
        </div>
        <div className="preferences-accordion-item__option iktos-preference-sub-row">
          <Checkbox
            data-e2e="checkbox-selectivity"
            checked={!!preferences.RX2_PREDICTION_PLAN_IKTOS_REGIO || false}
            label="Only regioselective reactions"
            onChange={() => onChange('RX2_PREDICTION_PLAN_IKTOS_REGIO', !preferences.RX2_PREDICTION_PLAN_IKTOS_REGIO)}
          />
        </div>
        <hr className="preferences-accordion-item__separator" />
        <PreferencesTooltip
          name="starting-material-settings"
          label="Starting materials settings"
          ariaLabel={IK_PARAMS_SELECTIVITY_INFO_LABEL}
          content={[
            <span key="starting-materials">
              Starting Materials are key parameters that guide the creation of synthetic routes. The route search halts when all starting
              materials are available in chosen commercial libraries. Only routes with such starting materials are provided to the user.
              Starting materials are sourced from Reaxys Commercial Substances (RCS), with the option to include custom libraries as well.
            </span>,
          ]}
        />
        {isRCSAvailable && (
          <>
            {showRCSCheckbox() ? (
              <div className="preferences-accordion-item__option iktos-preference-sub-row">
                <Checkbox
                  data-e2e="checkbox-rcs"
                  checked={isRCSChecked()}
                  disabled={isRCSChecked() && isSingleLibCheckBoxChecked()}
                  onChange={() => {
                    if (preferences.isRCSUnchecked) {
                      onChange('isRCSUnchecked', false);
                    } else {
                      if (preferences.isRCSUnchecked === undefined && enabledLibrariesRcs.length === 0) {
                        const optionIndex = predictionPlanOptions.findIndex((lib) => [RCS10D, RCSANY].indexOf(lib.name) >= 0);
                        if (optionIndex >= 0) {
                          onChange(predictionPlanOptions[optionIndex]?.property, true);
                          setPredictionPlanOptions((prev) => setImmutable(prev, `${optionIndex}.value`, true));
                        }
                      }
                      onChange('isRCSUnchecked', true);
                    }
                  }}
                  label="Reaxys Commercial Substances (RCS)"
                />
              </div>
            ) : (
              <div className="preferences-accordion-item__option iktos-preference-sub-row">Reaxys Commercial Substances (RCS)</div>
            )}
            <div className="preferences-accordion-item__option rcs-accordion iktos-preference-rcs-sub-row">
              <PreferencesTooltip
                name="max-shipping-time"
                label="Max shipping time"
                ariaLabel={IK_PARAMS_MAX_SHIPPING_TIME}
                content={[
                  <span key="max-shipping-time">
                    With the default settings the algorithm uses all starting materials from Reaxys Commercial Substances (RCS), that can be
                    shipped within up to 10 days. Switch to &quot;any&quot;, if you want to use all RCS compounds. You may also enter a
                    pricing limit (Max. price per gram) that filters the list of available starting materials to the given limit.
                  </span>,
                ]}
              />
            </div>
            <div className="preferences-accordion-item__option iktos-preference-mst-sub-row">
              <fieldset className="synthesis-planner-predicted__fieldset">
                <legend className="sr-only">{ProfileSettingsConfig.Labels.maxShippingTime}</legend>
                {predictionPlanOptions
                  ?.filter((block) => [RCS10D, RCSANY].indexOf(block.name) >= 0)
                  .map((element, idx) => (
                    <RadioButtonGroup.Option
                      key={element.name}
                      className="checkbox_inline_right"
                      data-e2e="checkbox-max-shipping-time"
                      checked={enabledLibrariesRcs && enabledLibrariesRcs?.filter((item) => item.name === element.name).length > 0}
                      label={<span>{maxShippingTimeDisplayHelperObj[element.name]}</span>}
                      disabled={preferences.isRCSUnchecked || enabledLibrariesRcs.length === 0}
                      onChange={(selected) => {
                        if (selected) {
                          handleMaxShippingTimeChange(idx, true);
                        }
                      }}
                      inputProps={{ 'data-testid': `checkbox-${element.name?.toLowerCase()}` }}
                    />
                  ))}
                {!find(predictionPlanOptions, (item) => item.name === RCSANY) && (
                  <RadioButtonGroup.Option
                    key={RCSANY}
                    className="checkbox_inline_right"
                    data-e2e="checkbox-max-shipping-time"
                    checked={false}
                    label={<span>{maxShippingTimeDisplayHelperObj[RCSANY]}</span>}
                    disabled
                    inputProps={{ 'data-testid': `checkbox-${RCSANY.toLowerCase()}` }}
                  />
                )}
              </fieldset>
            </div>
            <div className="preferences-accordion-item__option iktos-preference-rcs-sub-row">
              <TextInput
                value={preferences.RX2_PREDICTION_PLAN_IKTOS_PRICE || ''}
                label="Max. Price per gram:"
                isLabelInline
                size="small"
                data-e2e="iktos-max-price-per-gram-input"
                placeholder="any"
                className="iktos-max-price-per-gram-input"
                onChange={(e) => onChange('RX2_PREDICTION_PLAN_IKTOS_PRICE', e.target.value)}
                disabled={preferences.isRCSUnchecked || enabledLibrariesRcs.length === 0}
              />
              <PreferencesTooltip
                name="max-price"
                label="($/g)"
                ariaLabel={PREDICTED_PARAMS_MAX_PRICE_LABEL}
                content={[
                  <span key="max-price">
                    The “Max. Price per gram” parameter will limit the building block pool to commercial substances that do not cost more
                    than the given maximum price per gram. “Any” indicates that the filter is not active. Please click the edit box to enter
                    any integer numbers &gt; 0. Decimals are not supported.
                  </span>,
                ]}
              />
            </div>
            <div
              className={classnames('max-price-gm-validation-messege', {
                'not-visible': isValidMaxPricePerGram,
              })}>
              Please enter integers &gt;0 only
            </div>
            <div className="accordion-section-margin" />
          </>
        )}
        {isCustomBuildingBlocksAvailable() && (
          <div className="preferences-accordion-item__option iktos-preference-sub-row iktos-building-block-list">
            {showRCSCheckbox()
              ? predictionPlanOptions
                  ?.filter((block) => [RCS10D, RCSANY].indexOf(block.name) < 0)
                  .map((element, idx) => {
                    const checkboxStateChecked = Boolean(
                      enabledLibrariesWithoutRcs && enabledLibrariesWithoutRcs.filter((item) => item.name === element.name)?.length > 0
                    );
                    return (
                      <Checkbox
                        className="checkbox_no-padding"
                        key={element.name}
                        disabled={checkboxStateChecked && isSingleLibCheckBoxChecked()}
                        checked={checkboxStateChecked || false}
                        onChange={(event) => {
                          handlePlanOptionChange(idx, event?.currentTarget?.checked);
                        }}
                        label={`${element.label} (${element.name})`}
                        data-e2e="checkbox-building-block"
                      />
                    );
                  })
              : predictionPlanOptions
                  ?.filter((block) => [RCS10D, RCSANY].indexOf(block.name) < 0)
                  .map((element) => <span key={element.name}>{`${element.label} (${element.name})`}</span>)}
          </div>
        )}
        {isRCSAvailable && isRPSAvailable && (
          <>
            <hr className="preferences-accordion-item__separator" />
            <div className="preferences-accordion-item__option synthesis-planner-predicted-accordion-item__published-routes-integration">
              <PreferencesTooltip
                name="published-routes-integration"
                content={[
                  <div key="published-routes-integration">
                    <div className="u-els-font-size-caption u-els-margin-bottom">Published Routes Integration</div>
                    <div>
                      Merges literature routes with predicted routes, adding literature-based steps for non‑commercial intermediates and
                      linking them to the predicted pathway. It also creates combined routes by integrating established literature reactions
                      with computer‑generated steps, starting from your configured starting-material settings. Published routes are limited
                      to five steps and use the cheapest available building blocks.
                    </div>
                  </div>,
                ]}
                label="Published Routes Integration"
              />
            </div>
            <div className="preferences-accordion-item__option iktos-preference-sub-row">
              <SlideSwitch
                disabled={!isRCSChecked()}
                checked={preferences.isRPSChecked}
                onChange={onRPSSwitchChange}
                data-e2e="switch-published-routes-integration"
                label={
                  <>
                    <RPSSwitchIcon />
                    <span className="u-els-display-inline-block">Combine Predicted and Published routes</span>
                  </>
                }
                switchRight
              />
            </div>
          </>
        )}
        <hr className="preferences-accordion-item__separator" />
        <div className="preferences-accordion-item__option synthesis-planner-predicted-accordion-item__processing-time">
          <PreferencesTooltip
            name="processing-time"
            content={[
              <span key="processing-time">
                As the retrosynthesis process can work virtually indefinitely, a processing time limit has to be defined. By the Standard
                option, retrosynthesis computes predictive results for approximately 10 minutes. In case no results have been found with the
                standard option you can set the processing time to Extended, which will run for approximately 30 minutes.
              </span>,
            ]}
            label={ProfileSettingsConfig.Labels.processingTime}
          />
        </div>
        <div className="preferences-accordion-item__option iktos-preference-sub-row">
          {PREDICTED_IKTOS_PROCESSING_TIME_INTERVALS.map((interval) => (
            <RadioButtonGroup.Option
              key={interval.value}
              className="checkbox_inline_right"
              data-e2e="checkbox-processing-time"
              checked={interval.value === processingTimeValue || false}
              label={<span>{interval.label}</span>}
              onChange={() => onChange('RX2_PREDICTION_PLAN_IKTOS_PROCESSING_TIME', interval.value)}
            />
          ))}
        </div>
      </div>
    </ProfileSettingsAccordionItem>
  );
}

SynthesisPlannerPredictedIktos.propTypes = {
  preferences: PropTypes.object,
  options: PropTypes.object,
  editUser: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

export default SynthesisPlannerPredictedIktos;
